import {
  AccessibleContent,
  Button,
  Flex,
  Heading,
  IconAddLine,
  InlineList,
  Modal,
  Pill,
  ScreenReaderContent,
  Spinner,
  Tag,
  Text,
  TextInput,
  ToggleDetails,
} from "@instructure/ui";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Files } from "../../../../../services/FilesService";
import { QualitativeMeasureService } from "../../../../../services/QualitativeMeasureService";
import { fileWriter } from "../../../../../utils/FileUtils";
import { ApDataContext } from "../../../../../views/Plan";
import { Card } from "../../../../Card";
import {
  ERROR,
  useNotification,
} from "../../../../CustomHooks/NotificationProvider";
import { rteHasContent } from "../../../../RichTextArea/RichTextEditor";
import RichTextViewer from "../../../../RichTextArea/RichTextViewer";
import { GoalsSingleSelect } from "../../../../Selectors/GoalSelect";
import { ObjectiveSelect } from "../../../../Selectors/ObjectiveSelect";
import { QUALITATIVE, emailVerification } from "../../utils";

const QualMeasureTemplateCard = ({ templateFields }) => {
  const { sectionIndex, measureIndex, selectedCycle, handleCreateMeasure } =
    useContext(ApDataContext);
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [name, setName] = useState();
  const [uploadEvidence, setUploadEvidence] = useState();
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [triggerObjectives, setTriggerObjectives] = useState(false);
  const [email, setEmail] = useState("");
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [reviewers, setReviewers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(templateFields.name);
    setUploadEvidence(templateFields.upload_evidence ? "yes" : "no");
  }, [templateFields]);

  const handleAddAssignee = () => {
    // Checks to see if the email is valid
    if (emailVerification(email)) {
      setInvalidEmail(false);
      setReviewers((prevReviewers) => [...prevReviewers, email]);
      setEmail("");
    } else {
      setInvalidEmail(true);
    }
  };

  const handleGoalSelect = (goalId) => {
    setSelectedGoal(goalId);
    setSelectedObjective(null);
    setTriggerObjectives(true);
  };

  const handleObjectiveSelect = (objectiveId) => {
    setSelectedObjective(objectiveId);
  };

  const missingRequiredQualitativeFields = () => {
    const missing_fields = [];
    if (!selectedGoal) {
      missing_fields.push(t("assessment.goal"));
    }
    if (reviewers.length === 0) {
      missing_fields.push("Email");
    }
    if (missing_fields.length === 0) {
      return false;
    }
    let alert_message = "";
    const num_missing_fields = missing_fields.length;
    if (num_missing_fields === 1) {
      alert_message +=
        missing_fields[0] +
        t("notifications.assessments.missingMultipleFieldsErrorMessage");
    } else {
      missing_fields.forEach((field, idx) => {
        if (idx === num_missing_fields - 1) {
          alert_message +=
            t("assessment.and") +
            " " +
            field +
            t("notifications.assessments.missingSingleFieldErrorMessage");
        } else if (num_missing_fields === 2) {
          alert_message += field + " ";
        } else {
          alert_message += field + ", ";
        }
      });
    }
    showNotification(alert_message, ERROR);
    return true;
  };

  const handleQualitativeVerification = () => {
    if (!missingRequiredQualitativeFields()) {
      setSaving(true);
      const goal = selectedCycle.goals.find((g) => g.id === selectedGoal);
      const objective = goal.objectives.find((o) => o.id === selectedObjective);

      const noObjective = !selectedObjective || selectedObjective === "0";

      const measureAdd = {
        name: name,
        upload_evidence: uploadEvidence,
        goal: { id: selectedGoal, description: goal.description },
        objective: noObjective
          ? null
          : { id: selectedObjective, description: objective.description },
        assignees: [...reviewers].sort().join(","),
        sequence: measureIndex,
      };
      const measure = {
        planId: null,
        sectionId: null,
        qualitativeMeasure: measureAdd,
      };
      QualitativeMeasureService.createMeasure(measure)
        .then((newMeasure) => {
          if (templateFields.description) {
            const instructionsFile = fileWriter(
              templateFields.description,
              "response.html",
              "text/html"
            );
            Files.saveFile(
              instructionsFile,
              "qualitativeMeasure",
              newMeasure.ddl_id
            ).then(() => {
              newMeasure = {
                ...newMeasure,
                type: QUALITATIVE,
                instructions: templateFields.description,
              };
              handleCreateMeasure(newMeasure, sectionIndex, measureIndex);
            });
          } else {
            newMeasure = { ...newMeasure, type: QUALITATIVE };
            handleCreateMeasure(newMeasure, sectionIndex, measureIndex);
          }
        })
        .catch(() => {
          showNotification(
            t("notifications.assessments.createAssessmentError"),
            ERROR
          );
        })
        .finally(() => setSaving(false));
    }
  };

  return (
    <Card style={{ backgroundColor: "#FFFFFF", padding: "0" }}>
      <Flex wrap="wrap" padding="small">
        <Flex.Item size="100%">
          <Pill color="info" themeOverride={{ maxWidth: "30rem" }}>
            {t("assessment.qualitativeMeasure")}
          </Pill>
        </Flex.Item>
        <Flex.Item size="100%" margin="x-small 0 0 0">
          <Text as="div" size="small" lineHeight="double">
            <Text weight="bold" size="small">
              {t("assessment.name")}:{" "}
            </Text>
            {templateFields.name}
          </Text>
        </Flex.Item>
        <Flex.Item>
          <Text as="div" size="small" lineHeight="double">
            <Text weight="bold" size="small">
              {t("measures.uploadEvidence")}?:{" "}
            </Text>
            {templateFields.upload_evidence
              ? t("dialogYesNo.yes")
              : t("dialogYesNo.no")}
          </Text>
        </Flex.Item>
        {rteHasContent(templateFields.description) && (
          <Flex.Item size="100%" margin="xxx-small 0 0 0">
            <ToggleDetails
              summary={
                <Text weight="bold" size="small">
                  {t("assessment.instructions")}
                </Text>
              }
            >
              <RichTextViewer
                value={templateFields.description}
                ariaLabel={t("assessment.instructions")}
              />
            </ToggleDetails>
          </Flex.Item>
        )}
        <Flex.Item size="100%" margin="small 0 0 0">
          <Heading as="h3" level="h5" border="bottom">
            {t("assessment.with")}
          </Heading>
          <Flex gap="small" wrap="wrap" margin="small 0">
            <Flex.Item shouldGrow shouldShrink>
              <GoalsSingleSelect
                id={`goal-select-section-${sectionIndex}-measure-${measureIndex}`}
                selectHandler={handleGoalSelect}
                goalsList={selectedCycle.goals}
                selectedGoal={selectedGoal}
                size="small"
                isRequired
              />
            </Flex.Item>
            <Flex.Item shouldGrow shouldShrink>
              <ObjectiveSelect
                id={`objective-select-section-${sectionIndex}-measure-${measureIndex}`}
                selectedObjective={selectedObjective}
                selectedGoal={selectedGoal}
                selectedCycle={selectedCycle}
                selectHandler={handleObjectiveSelect}
                trigger={triggerObjectives}
                setTrigger={setTriggerObjectives}
                size="small"
              />
            </Flex.Item>
          </Flex>
        </Flex.Item>
        <Flex.Item size="100%">
          <Heading as="h3" level="h5" border="bottom" margin="0 0 small 0">
            {t("measures.assignedTo")}
          </Heading>
          <Flex gap="small">
            <Flex.Item size="25%">
              <TextInput
                type="email"
                renderLabel={
                  <ScreenReaderContent>
                    {t("assessment.email")}
                  </ScreenReaderContent>
                }
                placeholder={t("assessment.email")}
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                messages={
                  invalidEmail
                    ? [{ text: t("assessment.invalidEmail"), type: "error" }]
                    : []
                }
              />
            </Flex.Item>
            <Flex.Item align="start">
              <Button
                size="small"
                renderIcon={IconAddLine}
                onClick={handleAddAssignee}
              >
                {t("global.add")}
              </Button>
            </Flex.Item>
          </Flex>
        </Flex.Item>
        {reviewers.length > 0 && (
          <Flex.Item size="100%">
            <InlineList>
              {reviewers.map((email, key) => (
                <InlineList.Item key={key} margin="small 0 0 0">
                  <Tag
                    text={
                      <AccessibleContent alt={t("assessment.deleteAssessor")}>
                        {email}
                      </AccessibleContent>
                    }
                    dismissible
                    onClick={() => {
                      setReviewers((prevReviewers) =>
                        prevReviewers.filter((reviewer) => reviewer !== email)
                      );
                    }}
                  />
                </InlineList.Item>
              ))}
            </InlineList>
          </Flex.Item>
        )}
      </Flex>
      <Modal.Footer>
        <Button
          color="primary"
          size="small"
          onClick={handleQualitativeVerification}
          disabled={saving}
        >
          {saving ? (
            <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
          ) : (
            t("global.save")
          )}
        </Button>
      </Modal.Footer>
    </Card>
  );
};

export default QualMeasureTemplateCard;
